import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import ErrorModal from '../ui/ErrorModal';
import {
  CenteredWelcomeShell,
  HCard,
  HField,
  HPrimaryButton,
  HSecondaryButton,
} from '../h';
import { API_BASE_URL } from '../../firebase/firebaseConfig';

/* v9 mockup 1.1 — Welcome / sign-in.

   Behaviour preserved from the previous implementation:
   - GET ?email=<...> pre-populates the field (for the link in the verify-code email).
   - POST /user/start — if `allowed`, advance to verify; otherwise show inline disabled
     state with the support message and a "Try a different email" reset button.
   - Network errors surface in an ErrorModal. */
function SignIn({ onEmailSubmit }) {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [isHandlingSubmit, setIsHandlingSubmit] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) setEmail(emailParam);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsHandlingSubmit(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(`/user/start returned ${response.status}`);
      const data = await response.json();

      if (data.allowed) {
        onEmailSubmit(email);
      } else {
        setErrorMessage(
          'You have not purchased or been invited to manage any Higlo devices, or your account has not been activated yet. Please contact support at hq@higlo.co with any questions.',
        );
        setEmailDisabled(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsHandlingSubmit(false);
    }
  };

  const handleReset = () => {
    setEmailDisabled(false);
    setErrorMessage('');
    setShowErrorModal(false);
  };

  return (
    <CenteredWelcomeShell
      title="Welcome"
      subtitle="Sign in to confirm shipping, set up your Hearth devices, or manage your Higlo spaces."
    >
      <form onSubmit={handleSubmit} noValidate>
        <HField
          label="Email address"
          id="signin-email"
          type="email"
          placeholder="name@domain.com"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={emailDisabled}
          required
          className="mb-3"
        />

        {!emailDisabled && (
          <HPrimaryButton
            type="submit"
            disabled={isHandlingSubmit || !email}
            className="w-full"
          >
            {isHandlingSubmit ? 'Sending code…' : 'Continue'}
          </HPrimaryButton>
        )}

        {emailDisabled && errorMessage && (
          <HCard variant="warn" className="p-4 mt-2">
            <p className="text-[13px] text-slate-700 leading-relaxed mb-3">
              {errorMessage}
            </p>
            <HSecondaryButton onClick={handleReset} className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Try a different email
            </HSecondaryButton>
          </HCard>
        )}
      </form>

      <p className="text-[11px] text-slate-400 mt-6 text-center leading-relaxed">
        No password needed. We&rsquo;ll email you a one-time code.
      </p>

      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
      />
    </CenteredWelcomeShell>
  );
}

export default SignIn;
